import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import StandardModal, { ModalSize, ModalButtonType } from '../../Fundamentals/Modal/StandardModal';
import EmailTemplate from '../../FormComponents/EmailTemplate';
import useModal from '../../../Hooks/useModal';
import customerActions from '../../../Actions/customerActions';
import commonActions from '../../../Actions/commonActions';
import { RegexLib } from '../../../Constants/formValidationRules';
import type { EmailTemplateModel, VbInvoice } from '../../../Containers/Customer/CustomerTransactions.types';
import { useAppDispatch } from '../../../Store/types';

const EMAIL_REGEX = new RegExp(RegexLib.email);

const htmlToText = (html: string): string => (new DOMParser().parseFromString(html, 'text/html').body.textContent ?? '').trim();

interface SendInvoiceCopyModalProps {
    customerNumber: string;
    invoice: VbInvoice;
}

const SendInvoiceCopyModal = ({ customerNumber, invoice }: SendInvoiceCopyModalProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { closeModal } = useModal();

    const [form, setForm] = useState<EmailTemplateModel | null>(null);
    const [isSending, setIsSending] = useState(false);

    useEffect(() => {
        let active = true;
        dispatch(customerActions.fetchInvoiceEmailTemplate(customerNumber, invoice.solumInvoiceOCR, invoice.vbInvoiceOCR, invoice.invoiceNumber, invoice.id))
            .then((json: EmailTemplateModel) => {
                if (active) {
                    setForm(json);
                }
            })
            .catch(() => {
                if (active) {
                    dispatch(commonActions.activateModal(null));
                }
            });
        return () => {
            active = false;
        };
    }, [dispatch, customerNumber, invoice]);

    const updateField = (field: keyof EmailTemplateModel, value: string) => {
        setForm((prev) => (prev === null ? prev : { ...prev, [field]: value }));
    };

    const isValid =
        form !== null && EMAIL_REGEX.test((form.to ?? '').trim()) && (form.from ?? '').trim() !== '' && (form.subject ?? '').trim() !== '' && htmlToText(form.body ?? '') !== '';

    const handleSubmit = () => {
        if (form === null || !isValid) {
            return;
        }
        setIsSending(true);
        dispatch(
            customerActions.sendInvoiceCopy({
                ...form,
                to: (form.to ?? '').trim(),
                subject: (form.subject ?? '').trim(),
            })
        )
            .then(() => {
                closeModal();
                dispatch(commonActions.showSuccess(t('customertransactions.copySent')));
            })
            .catch(() => setIsSending(false));
    };

    return (
        <StandardModal
            title={t('customertransactions.sendCopy')}
            size={ModalSize.LARGE}
            type={ModalButtonType.SEND}
            isLoading={form === null || isSending}
            onSubmit={handleSubmit}
            submitDisabled={!isValid}
        >
            <EmailTemplate data={form} onChange={updateField} />
        </StandardModal>
    );
};

export default SendInvoiceCopyModal;
